import type { ReactElement } from 'react';
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { buttonClassName } from '../../components/primitives/index.js';
import { BrandMark, MotifField, MotifRule } from '../../components/brand/index.js';
import { Icon, machineMotif, type IconName } from '../../components/icons/index.js';
import { fetchTaxonomy } from '../../lib/api/queries/catalog.js';

/**
 * `/` — the product entry point.
 *
 * Deliberately NOT a marketing template. Every claim on this page is one the
 * product can keep: there are no counts, no testimonials and no statistics,
 * because none exist to state truthfully. What it does instead is answer, in
 * this order, the four questions a first-time visitor has — what is this, what
 * can I hire, how does a rental run, and why is it different from ringing round
 * three yards.
 *
 * **The one thing on this page that is fetched is the catalogue's own
 * taxonomy** (P-Q004, public). Category tiles are real: they are the categories
 * the platform classifies machines under, and each one leads to the search that
 * will honestly report what is published in it — including nothing. Nothing
 * about a tile claims inventory. If the read fails or returns nothing, the
 * section is simply absent; a landing page does not show an error state for a
 * decoration.
 *
 * The lifecycle below is RENTAL_STATE_MACHINE.md's booking machine described in
 * plain words. It stops where the product stops: an approved bill. Nothing
 * implies a payout or a refund, because neither exists (OD-6).
 */

interface Step {
  ordinal: string;
  icon: IconName;
  title: string;
  body: string;
}

const LIFECYCLE: Step[] = [
  {
    ordinal: '01',
    icon: 'machine',
    title: 'Find',
    body: 'Search published machines by category, manufacturer, model or specification. Check the owner’s calendar for the dates you need.',
  },
  {
    ordinal: '02',
    icon: 'requests',
    title: 'Agree',
    body: 'Ask the owner for terms. They quote; you accept or counter. When both sides agree, the rate, the dates and the policies are recorded as they stood — and stay that way.',
  },
  {
    ordinal: '03',
    icon: 'onHire',
    title: 'Handover on record',
    body: 'Photographs, the meter reading and a condition checklist are captured by both parties before the machine moves. That record is what an argument is settled against.',
  },
  {
    ordinal: '04',
    icon: 'bookings',
    title: 'Return and bill',
    body: 'The same evidence is captured on return. The bill is built from the agreed terms and the recorded meter, then approved by both sides.',
  },
];

interface Difference {
  icon: IconName;
  title: string;
  body: string;
}

const DIFFERENCES: Difference[] = [
  {
    icon: 'allClear',
    title: 'Both parties capture, not one',
    body: 'Handover and return are confirmed by the customer and the owner. Neither can record a condition the other has not seen.',
  },
  {
    icon: 'published',
    title: 'Agreed terms are frozen',
    body: 'The rate you agreed in March is the rate you are billed at in March, even if the owner changes their rate card in April. Historical terms are stored as a copy, not a reference.',
  },
  {
    icon: 'rateCard',
    title: 'The meter is the measure',
    body: 'Usage is billed against recorded meter readings, not an estimate. Readings are append-only: a mistake is corrected by a new reading, never by editing the old one.',
  },
  {
    icon: 'draft',
    title: 'Nothing is decided off the record',
    body: 'Every step — the offer, the handover, the readings, the bill — is a dated entry either side can read back. There is no version of events that only one party holds.',
  },
];

/** Up to eight. A rail longer than that is a filter list wearing tiles. */
const CATEGORY_LIMIT = 8;

export function LandingPage(): ReactElement {
  const taxonomy = useQuery({
    queryKey: ['taxonomy'],
    queryFn: () => fetchTaxonomy(),
    retry: false,
  });
  const categories = (taxonomy.data ?? [])
    .filter((node) => node.level === 'category')
    .slice(0, CATEGORY_LIMIT);

  return (
    <div className="landing">
      {/*
        The hero states the product, names its difference and offers one
        obvious way in. It carries the motif rather than a photograph, for the
        reason every other surface does: the platform holds no machine imagery,
        and a stock excavator would be a claim about equipment that is not here.
      */}
      <section className="landing-hero enter" aria-labelledby="landing-title">
        <MotifField height={420} />
        <div className="landing-hero__inner">
          <p className="landing-hero__eyebrow">
            <BrandMark size={16} />
            Equipment rental marketplace
          </p>
          <h1 id="landing-title" className="landing-hero__title">
            Hire the machine. Keep the handover on record.
          </h1>
          <p className="landing-hero__lede">
            Excavators, loaders, cranes and compaction plant, hired direct from the owners who run
            them. Every rental carries a dated record of what was agreed, what condition the machine
            was in and what the meter read — so the bill at the end is a matter of record rather
            than of recollection.
          </p>
          <div className="landing-hero__actions">
            <Link to="/equipment" className={buttonClassName('primary', 'lg')}>
              Browse machines
            </Link>
            <a href="#how-it-works" className={buttonClassName('secondary', 'lg')}>
              How it works
            </a>
          </div>
          <p className="landing-hero__note">
            Browsing needs no account. You sign in when you want to ask an owner for terms.
          </p>
        </div>
      </section>

      {categories.length === 0 ? null : (
        <section className="landing-section" aria-labelledby="landing-categories">
          <h2 id="landing-categories" className="landing-section__title">
            Browse by category
          </h2>
          <p className="landing-section__lede">
            The catalogue’s own categories. Follow one to see exactly what owners have published in
            it.
          </p>
          <ul className="category-rail category-rail--landing stagger">
            {categories.map((node) => (
              <li key={node.code}>
                <Link
                  to={`/equipment?category=${encodeURIComponent(node.code)}`}
                  className="category-tile liftable"
                >
                  <span className="category-tile__glyph" aria-hidden="true">
                    <Icon name={machineMotif(node.name)} size="md" />
                  </span>
                  <span className="category-tile__label">{node.name}</span>
                </Link>
              </li>
            ))}
          </ul>
        </section>
      )}

      {/*
        The lifecycle reads as a SEQUENCE, not as four unrelated facts. The rail
        behind the ordinals is what makes the order the first thing the eye
        picks up, which is the whole point of the section.
      */}
      <section className="landing-section" id="how-it-works" aria-labelledby="landing-lifecycle">
        <h2 id="landing-lifecycle" className="landing-section__title">
          How a rental runs
        </h2>
        <p className="landing-section__lede">
          Four steps, and the product records every one of them.
        </p>
        <ol className="lifecycle stagger">
          {LIFECYCLE.map((step) => (
            <li key={step.ordinal} className="lifecycle__step">
              <span className="lifecycle__marker" aria-hidden="true">
                <Icon name={step.icon} size="md" />
              </span>
              <span className="lifecycle__ordinal numeric" aria-hidden="true">
                {step.ordinal}
              </span>
              <h3 className="lifecycle__title">{step.title}</h3>
              <p className="lifecycle__body">{step.body}</p>
            </li>
          ))}
        </ol>
        <p className="landing-note">
          The rental record ends at an approved bill. Payment settlement between the platform and
          owners is not part of the product yet, and nothing here implies otherwise.
        </p>
      </section>

      <section className="landing-section landing-section--sunken" aria-labelledby="landing-why">
        <div className="landing-section__inner">
          <h2 id="landing-why" className="landing-section__title">
            Why this marketplace is different
          </h2>
          <p className="landing-section__lede">
            Equipment disputes are almost always disputes about evidence: who damaged what, how many
            hours were run, which rate applied. Four rules decide that here.
          </p>
          <ul className="difference-grid stagger">
            {DIFFERENCES.map((item) => (
              <li key={item.title} className="difference">
                <span className="difference__glyph" aria-hidden="true">
                  <Icon name={item.icon} size="md" />
                </span>
                <h3 className="difference__title">{item.title}</h3>
                <p className="difference__body">{item.body}</p>
              </li>
            ))}
          </ul>
        </div>
      </section>

      {/*
        A marketplace has two sides and this page once named neither, so an
        equipment owner arriving here had no way to tell the product wanted
        them.
      */}
      <section className="landing-section" aria-labelledby="landing-roles">
        <h2 id="landing-roles" className="landing-section__title">
          Two sides of the same record
        </h2>
        <div className="role-grid">
          <article className="role-panel">
            <MotifRule width={32} />
            <p className="role-panel__who">Customer</p>
            <h3 className="role-panel__title">Find and rent machines</h3>
            <p className="role-panel__body">
              Search published plant by category, model or specification. Check the dates, ask the
              owner for terms, and accept the offer you agree with. The rate you agreed is the rate
              you are billed at.
            </p>
            <Link to="/equipment" className={buttonClassName('secondary', 'md')}>
              Browse machines
            </Link>
          </article>
          <article className="role-panel role-panel--owner">
            <MotifRule width={32} />
            <p className="role-panel__who">Owner</p>
            <h3 className="role-panel__title">List machines and receive demand</h3>
            <p className="role-panel__body">
              List your fleet, set rate cards and availability, and answer real rental requests.
              Every handover and return is captured by both parties, so a disagreement is settled
              against a record rather than an argument.
            </p>
            <Link to="/login" className={buttonClassName('secondary', 'md')}>
              Sign in to manage your fleet
            </Link>
          </article>
        </div>
      </section>

      <section className="landing-closing" aria-labelledby="landing-start">
        <MotifField height={200} />
        <div className="landing-closing__inner">
          <h2 id="landing-start" className="landing-section__title">
            Start with the machine
          </h2>
          <p className="landing-section__lede">
            Search the published fleet by what the job needs — category, manufacturer, model, or a
            specification such as operating weight or lift height.
          </p>
          <Link to="/equipment" className={buttonClassName('primary', 'lg')}>
            Browse machines
          </Link>
        </div>
      </section>
    </div>
  );
}
